/**
 * Billing exclusion — which delivered orders are not billable shipments and so
 * never count as Unbillable / Missing BOL on the dashboard.
 *
 * A BOL describes the cargo; an empty trailer has none. Emptiness outranks the
 * customer's "requires documentation" flags (DTP-108018). An order is excluded
 * when:
 *   1. it is a bobtail (`trailer_type.kind === 'bobtail'`); or
 *   2. every leg is planned empty (`legs.is_loaded === false`) — the
 *      dispatcher's declaration, which survives split trailer history (e.g. a
 *      trailer-number typo fix); or
 *   3. the trailer's load status at delivery is `empty`, from
 *      `order_load_status_at_delivery` (latest `trailer_events` row with a load
 *      status at or before arrival).
 *
 * The trailer's live `trailers.load_status` is deliberately never consulted:
 * it drifts as the trailer is reused, which made weeks-old delivered orders
 * reappear as Unbillable. No evidence keeps the order flagged.
 *
 * Known limitation: (3) resolves the order-level trailer (`orders.trailer_id`,
 * which mirrors leg 1) at the order's arrival, so legs 2+ on a different
 * trailer aren't reflected.
 */

/** True when the order has legs and every one is planned empty. */
export function isPlannedEmpty(legLoads: readonly (boolean | null)[] | undefined): boolean {
  return !!legLoads && legLoads.length > 0 && legLoads.every((l) => l === false);
}

export function isExcludedFromBilling(
  order: { trailer_type?: { kind?: string | null } | null },
  legLoads: readonly (boolean | null)[] | undefined,
  loadStatusAtDelivery: string | null | undefined,
): boolean {
  if (order.trailer_type?.kind === "bobtail") return true;
  if (isPlannedEmpty(legLoads)) return true;
  return loadStatusAtDelivery === "empty";
}
